/**
 * FormField - Molecule that wraps the Input atom with consistent vertical spacing.
 * Adds a bottom margin (mb-16px) so form fields stack with uniform gaps.
 * Use this instead of Input directly when building multi-field forms.
 *
 * The `name` prop is required and doubles as the input's `id`, ensuring
 * proper label association and form data keying.
 *
 * @example
 * <FormField name="email" label="Email Address" type="email" required />
 * <FormField name="phone" label="Phone" helperText="Include area code" />
 */
import React from 'react';
import { Input, InputProps } from '../../atoms';

/**
 * Props for FormField.
 * Extends all InputProps so every Input feature (label, error, helperText, etc.) is available.
 */
export interface FormFieldProps extends InputProps {
	/** Unique field name used for id generation and form data submission */
	name: string;
}

const FormField: React.FC<FormFieldProps> = ({ name, label, error, helperText, required, className = '', ...rest }) => {
	return (
		// mb-16px provides consistent vertical rhythm between stacked form fields.
		// Additional classes can be merged via the className prop.
		<div className={['form-field', 'mb-16px', className].join(' ')} data-ref="molecule-form-field">
			{/* Delegates all rendering to Input; name is passed as both `id` and `name`
				so the label's htmlFor and form submission key are always in sync. */}
			<Input id={name} label={label} error={error} helperText={helperText} required={required} name={name} {...rest} />
		</div>
	);
};

export default FormField;
